import {
    clearQueryCache,
    invalidateTags,
    type CacheTag
} from '@/lib/queryCache'

/**
 * Which cached data a successful write makes stale, by method and path.
 * Called from the two places every request goes through: the generated
 * API client's middleware and AuthenticatedApiClient (PocketBase). Call
 * sites never clear the cache themselves.
 *
 * A write that matches no rule clears the whole cache: a new endpoint is
 * safe (just uncached) until someone maps it.
 */
type Rule = { path: RegExp; tags: CacheTag[] | 'all' }

const RULES: Rule[] = [
    // Sign-in / token exchange: who is asking (and what they may see)
    // may have changed.
    { path: /\/api\/v1\/auth\/exchange$/, tags: 'all' },
    // Login, photo, level, bio: embedded in previews, coach cards, profiles.
    { path: /\/api\/v1\/auth\/update$/, tags: ['authors', 'own'] },
    // Programme previews (publish, edit, unpublish).
    {
        path: /\/collections\/v1CommunityLessonsPreview(\/|\?|$)/,
        tags: ['previews']
    },
    // Coach attributes and coach status.
    {
        path: /\/api\/v1\/requests\/coach\/settings$/,
        tags: ['coaches', 'own']
    },
    { path: /\/api\/v1\/coach\/application$/, tags: ['coaches', 'own'] },
    {
        path: /\/api\/v1\/coach\/applications\/[^/]+\/decision$/,
        tags: ['coaches']
    },
    // A new request or a status change moves the coach request stats.
    { path: /\/api\/v1\/requests$/, tags: ['stats', 'own'] },
    { path: /\/api\/v1\/requests\/[^/]+\/status$/, tags: ['stats', 'own'] },
    // Starting, stopping, training, archiving: completions and history.
    { path: /\/api\/v1\/datalake(\?|$)/, tags: ['stats', 'own'] },
    // Events (reflections' complexity ratings...) move stats. Visits and
    // coach engagement go through telemetryClient and never reach here.
    { path: /\/api\/v1\/sm\/[^/?]+/, tags: ['stats'] },
    // A message moves the request's last activity in the own lists.
    { path: /\/api\/v1\/requests\/[^/]+\/messages$/, tags: ['own'] },
    // A purchase or subscription change adds to the user's own data.
    { path: /\/api\/v1\/payments\//, tags: ['own'] },
    // Taking a referral code changes the user's referral programmes.
    { path: /\/api\/v1\/referrals\//, tags: ['own'] },
    // The payout wallet is part of the cached payouts screen.
    { path: /\/api\/v1\/requests\/coach\/payout-wallet$/, tags: ['own'] },
    // Writes that touch nothing cached here.
    { path: /\/api\/v1\/motivation\/send\//, tags: [] }
]

/**
 * VictoriaMetrics sees an event a little after the write that caused it.
 * A refetch inside that window would cache the old totals for a whole ttl,
 * so stats are invalidated once more after it.
 */
const STATS_SETTLE_MS = 15_000
let settleTimer: ReturnType<typeof setTimeout> | undefined

export function invalidateForWrite(method: string | undefined, url: string) {
    const verb = (method || 'GET').toUpperCase()
    if (verb === 'GET' || verb === 'HEAD' || verb === 'OPTIONS') return

    let path = url
    try {
        const parsed = new URL(url, window.location.origin)
        path = parsed.pathname + parsed.search
    } catch {
        // keep the raw url
    }

    const rule = RULES.find((r) => r.path.test(path))
    const tags = rule ? rule.tags : 'all'
    if (tags === 'all') {
        clearQueryCache()
        return
    }
    invalidateTags(tags)
    if (tags.includes('stats')) {
        clearTimeout(settleTimer)
        settleTimer = setTimeout(
            () => invalidateTags(['stats']),
            STATS_SETTLE_MS
        )
    }
}
